// D6-3 — THE ANALYTICS TAB, **REBUILT AROUND THE SITE BY HER DASHBOARD ITEM'S SURFACE 2.** Her own
// order of importance, top to bottom: **(a) site traffic at the very top**, (b) the funnels, (c) the
// whole journey in one row, and **(d) everything else — the two room-and-generation tables — at the
// bottom in a collapsed section.** Growth, Lala volume and retention basics keep their places, since
// her (d) names exactly two tables and nothing else moved (scope rule 4). Ranged, CSV-exportable.
//
// THREE THINGS THIS TAB IS, AND EACH ONE IS A RULING RATHER THAN A CHOICE:
//   1. **IT READS THE ONE RANGE.** The range is dashboard state; this component is handed the query
//      string and never keeps a period of its own. The tab that had its own 1/7/30/90 buttons is
//      what this replaces — two range controls on one dashboard is two answers to "this month".
//   2. **ZERO VENDOR CALLS.** `/api/owner/command/analytics` reads stored aggregates only (D6-5).
//   3. **THE CSV IS THE TABLE ON SCREEN**, built from the payload already loaded (`toCsv`), so the
//      file and the page cannot disagree.
//
// NO MEMBER IS LISTED HERE. Her D6-3: members appear ONLY under Members.
import { useCallback, useEffect, useState } from "react";
import { loadOwnerData, ownerLoadNote } from "../lib/owner-load";
import { Download, RefreshCw } from "lucide-react";
import { freshnessLabel, toCsv, csvFilename } from "../commandCenter.js";
// CE-4 / ITEM 16 — her failures, in her words. The sentences live in the shared law beside the
// reason roster the server normalises into, so the screen and the column can never hold two opinions
// about what a reason means (R28/R29).
// SURFACE 3, CLAUSE 1 — the failures TABLE left this tab for the Alerts surface, so `failureLine`
// and `FAILURES_UNREAD` came out with it (R8's ledger rule: an extraction that leaves its imports
// behind pays the ceiling). `failureRows` STAYS, because the export still carries every table this
// tab is a record of — the surface moved, the record did not (R18).
import { failureRows } from "../generationFailures.js";
import { downloadCsv } from "../lib/downloads";
import { OwnerBars } from "./OwnerBars";
// F5 — her funnel report card. Its own component and its own read, because its numbers come off
// `funnel_leads` rather than off the analytics aggregate, and it is handed THIS tab's range so the
// dashboard keeps one period control (D6-3).
import { OwnerFunnels } from "./OwnerFunnels";
// HER DASHBOARD ITEM, SURFACE 2(a). Hookless and handed THIS tab's payload, so the dashboard keeps
// one range control (D6-3) and a gate can CALL it rather than grep for it.
import { OwnerTraffic, type TrafficPayload } from "./OwnerTraffic";
// SURFACE 2(c) and 2(d). Both hookless and both handed this tab's own payload, for OwnerTraffic's
// reason: the dashboard keeps ONE range control and a gate can CALL them.
import { OwnerLadder, type LadderPayload } from "./OwnerLadder";
import { OwnerTalkFunnel, type TalkFunnelPayload } from "./OwnerTalkFunnel";
// F8 — her morning sends per day, off DT-1b's beacon. Hookless and handed this tab's payload, for
// the same reason as the two above it.
import { OwnerPushDelivery, type PushDeliveryPayload } from "./OwnerPushDelivery";
// CADENCE-1, HER CLAUSE 5 — her never-paid cliffhanger cadence's five figures. Hookless and handed
// this tab's payload, for the same reason as the three above it: the dashboard keeps ONE range
// control and a gate can CALL it rather than grep for it.
import { OwnerCadence, type CadencePayload } from "./OwnerCadence";
import { OwnerAnalyticsMore } from "./OwnerAnalyticsMore";

type Day = { day: string; signups?: number; turns?: number };
type Analytics = {
  range: { key: string; label: string; days: number; startMs: number; endMs: number };
  rooms: Array<{ room: string; visits: number; generations: number }>;
  // SURFACE 2(a). `null` is a read that could not be made — never zero visitors (R18).
  traffic: TrafficPayload | null;
  // SURFACE 2(c). Same three states, for the same reason: four zeroes on the summary row of her
  // business is a thing she would act on, and a refused read is not four zeroes.
  ladder: LadderPayload | null;
  talkFunnel: TalkFunnelPayload | null;
  // F8. Same three states, for the same reason: a delivery table drawing itself empty reads as
  // "no morning reached anybody", which is the one wrong thing to say about the morning rail.
  pushDelivery: PushDeliveryPayload | null;
  // CADENCE-1. Same three states, for the same reason: a scoreboard drawing itself as five zeroes
  // reads as "the cadence reached nobody", which is the one wrong thing to say about the machine
  // she judges advertising spend by, and a refused read is not five zeroes.
  cadence: CadencePayload | null;
  byType: Array<{ type: string; count: number }>;
  // CE-4 / ITEM 16. `null` is a summary that ARRIVED AND COULD NOT BE READ — a different fact from
  // one that says zero (R18), and the only one of the two that must never render as "nothing failed".
  failures: { failures: number; membersAffected: number; byReason: Array<{ reason: string; count: number; members: number }> } | null;
  growth: Day[];
  lala: Day[];
  retention: { existing: number; returned: number; pct: number | null };
  takenMs: number;
};

const dayLabel = (v: string) => String(v || "").slice(0, 10);

export function OwnerAnalytics({
  rangeQuery, onDenied,
}: {
  rangeQuery: string; onDenied: () => void;
}) {
  const [data, setData] = useState<Analytics | null>(null);
  const [loading, setLoading] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [note, setNote] = useState("");
  // SURFACE 2(d) — **COLLAPSED BY DEFAULT, WHICH IS HER OWN WORD.** The state lives here rather than
  // in the section, so the section stays hookless and a gate can CALL it in both states.
  const [moreOpen, setMoreOpen] = useState(false);

  // R65: the answer is READ. A refused or dropped load used to leave `data` at null with nothing
  // said, so this tab drew itself out of `data?.x || []` and she read a business number that never
  // arrived. The paint is guarded by POSITION (R52) and the note lands on the freshness line, which
  // is the one line whose whole job is saying how current these numbers are.
  const load = useCallback(() => {
    setLoading(true);
    loadOwnerData<Analytics>(`/api/owner/command/analytics?${rangeQuery}`)
      .then((answer) => {
        if (!answer.ok && answer.kind === "denied") { onDenied(); return; }
        if (answer.ok) { setData(answer.data); setNow(Date.now()); }
        setNote(ownerLoadNote(answer));
      })
      .finally(() => setLoading(false));
  }, [rangeQuery, onDenied]);
  useEffect(() => { load(); }, [load]);

  const rooms = data?.rooms || [];
  const byType = data?.byType || [];
  const growth = data?.growth || [];
  const lala = data?.lala || [];
  const signups = growth.reduce((s, g) => s + Number(g.signups || 0), 0);
  const turns = lala.reduce((s, g) => s + Number(g.turns || 0), 0);
  const generations = byType.reduce((s, g) => s + Number(g.count || 0), 0);
  // CE-4 / ITEM 16: `null` for a summary that could not be read, so the note above the table says
  // that instead of the table drawing itself empty and reading as a clean window.
  const failures = failureRows(data?.failures);

  // The export carries EVERY table on the tab, one section per table, because she asked for the tab
  // to be exportable and not for one lucky table to be.
  const exportCsv = () => {
    if (!data) return;
    const text = [
      toCsv([{ key: "room", label: "Room" }, { key: "visits", label: "Visits" }, { key: "generations", label: "Generations" }], rooms),
      "",
      toCsv([{ key: "type", label: "Generation type" }, { key: "count", label: "Count" }], byType),
      "",
      // CE-4 / ITEM 16 — the failures ride the export too, because this tab's own ruling is that the
      // CSV carries EVERY table on it and not one lucky table. Her words, never the reason keys.
      toCsv([{ key: "label", label: "Reading that failed" }, { key: "value", label: "Count" }],
        (failures || []).map((f) => ({ label: f.label, value: f.count }))),
      "",
      toCsv([{ key: "day", label: "Day" }, { key: "signups", label: "Signups" }], growth.map((g) => ({ day: dayLabel(g.day), signups: g.signups ?? 0 }))),
      "",
      toCsv([{ key: "day", label: "Day" }, { key: "turns", label: "Lala turns" }], lala.map((g) => ({ day: dayLabel(g.day), turns: g.turns ?? 0 }))),
      "",
      // F8 — the morning rail rides the export too, because this tab's own ruling is that the CSV
      // carries EVERY table on it and not one lucky table. **The rate is the LAW's, never re-derived
      // here**, or the file and the page would be two answers to how many mornings landed (R28/R29).
      toCsv(
        [{ key: "day", label: "Day" }, { key: "attempted", label: "Sent" },
         { key: "delivered", label: "Reached the phone" }, { key: "failed", label: "Never arrived" },
         { key: "rate", label: "Got through" }],
        (data.pushDelivery?.days || []).map((d) => ({
          day: d.day, attempted: d.attempted, delivered: d.delivered, failed: d.failed,
          // A withheld rate is an EMPTY CELL and never a zero, exactly as it is on screen: a
          // spreadsheet reading 0% for a day nothing settled on is the same lie in a file.
          rate: d.rate === null ? "" : `${d.rate}%`,
        })),
      ),
    ].join("\r\n");
    downloadCsv(csvFilename("analytics", data.range), text);
  };

  return (
    <div className="owner-analytics">
      <div className="owner-overview__bar">
        <p className="owner-overview__fresh">
          {/* R65: THE NOTE SUPERSEDES THE FRESHNESS LABEL, and that ordering is the ruling. She taps
              Refresh, the load fails, and `data` is still the LAST good payload — so "Taken 2 minutes
              ago" would be printed over numbers that are now as old as the failure. A line about how
              current a number is must never survive a read that did not land. */}
          {note ? <span className="owner-fresh--failed" role="status">{note}</span>
            : data ? <>Taken {freshnessLabel(data.takenMs, now)} · {data.range?.label}</> : loading ? "Reading..." : "..."}
        </p>
        <span className="owner-analytics__acts">
          <button type="button" className="owner-link" onClick={load} disabled={loading}>
            <RefreshCw size={14} />{loading ? "Reading..." : "Refresh"}
          </button>
          <button type="button" className="owner-link" onClick={exportCsv} disabled={!data}>
            <Download size={14} />Export CSV
          </button>
        </span>
      </div>

      {/* HER DASHBOARD ITEM, SURFACE 2(a) — **SITE TRAFFIC AT THE VERY TOP**, her own order of
          importance, above every number on this tab. `data === null` is still loading and the
          component holds still; `data.traffic === null` is a read that arrived and could not be
          made, and it says so rather than drawing zeroes (R18). */}
      <OwnerTraffic traffic={data ? (data.traffic ?? null) : undefined} rangeLabel={data?.range?.label || "this window"} />

      <div className="owner-stats">
        <div className="owner-stat"><strong>{signups.toLocaleString("en-US")}</strong><span>Joined in this window</span></div>
        <div className="owner-stat"><strong>{generations.toLocaleString("en-US")}</strong><span>Generations run</span></div>
        {/* RETENTION BASICS, and it says what it measured. "42%" on its own is a number she cannot
            check; "42% — 61 of 145 who were already here came back" is one she can. */}
        <div className="owner-stat">
          <strong>{data?.retention?.pct === null || data?.retention?.pct === undefined ? "—" : `${data.retention.pct}%`}</strong>
          {/* "0 of 0 already here" reads as nobody came back. It is not: it is a window with nobody
              to come back, which is a different fact and says so. */}
          <span>
            Came back
            {!data?.retention ? "" : data.retention.existing
              ? ` · ${data.retention.returned.toLocaleString("en-US")} of ${data.retention.existing.toLocaleString("en-US")} already here`
              : " · nobody had joined before this window"}
          </span>
        </div>
      </div>

      {/* SURFACE 2(d) — **ROOMS RANKED AND GENERATIONS BY TYPE MOVED TO THE BOTTOM, COLLAPSED.**
          They used to sit here, directly under the stat row. Her order of importance puts the site
          first and this pair last, so the block is rendered at the foot of the tab instead. */}

      {/* CE-4 / ITEM 16 — **WHAT DID NOT REACH A MEMBER, AND WHY.** Full width and directly under
          the delivered work on purpose: the two tables answer the same window and the failures are
          the half that used to have no row anywhere on this site.

          THREE STATES, NOT TWO (R18/R65). `data === null` is a payload in flight and this whole tab
          holds still for it; `data.failures === null` is a summary that ARRIVED and could not be
          read and says so in its own sentence; a summary with zero in it is an ANSWER and earns
          "No generation failed in this window." Collapsing the middle one would print that clear
          sentence over a database that refused the read — the one wrong thing to tell her, and her
          own "never read green over failing members" clause one layer up from the rows. */}
{/* SURFACE 3, CLAUSE 1 — **THE FAILURES TABLE HAS LEFT THIS TAB.** Her ruling, in her own words:
          *failures are not statistics, they are flags.* It used to draw here, under the delivered
          work, as a bar chart of reason counts — a number she could look at and do nothing with,
          which is A2's exact complaint about every box on this dashboard.

          **IT IS A MOVE AND NEVER A REMOVAL, AND THE ORDER IS WHY THIS TAB COULD ONLY CHANGE
          SECOND.** The rows are on the Alerts surface now, with the MEMBER, the READING, the CAUSE
          and the remedy on the same card, raised as one standing alert that updates in place. The
          health tile that used to open this table opens that instead. Taking the table off here
          before that surface existed would have removed the only place those rows were visible,
          which is *dashboards read green over failing members* — her own headline clause — broken by
          the round that serves it (R19).

          **THE SUMMARY IS STILL ON THE PAYLOAD AND IS STILL EXPORTED**, so the RECORD half of this
          tab is untouched: what left is the surface, not the fact (R18). */}
      {/* F5 — **WHAT HER ADVERTISING BROUGHT IN**, per funnel, over the same window as everything
          above it. It sits directly under the failures and above growth on purpose: this is the top
          of the same journey — a stranger's details, then a signup, then a member — and reading the
          three tables in that order is how she can tell which door is doing the work. */}
      <OwnerFunnels rangeQuery={rangeQuery} onDenied={onDenied} />

      {/* SURFACE 2(c) — **THE WHOLE JOURNEY IN ONE ROW**, directly under the per-funnel card, which
          is her own order: the site, then each door, then the four numbers that summarise both.
          `data === null` is a payload in flight and this holds still; `data.ladder === null` is a
          read that arrived and could not be made and says so (R18). */}
      <OwnerLadder ladder={data ? (data.ladder ?? null) : undefined} />

      {/* >>> **THE /talk FUNNEL, TRUE (2026-09-05).** <<< It sits directly under the whole-journey
          row because it is the same journey through ONE door. **It replaces F4-8's board, which is
          DELETED**: that one drew the allotment columns — given the five, started, turns used, at
          the wall — and two of her five names had no reader at all and printed an apology instead,
          so it could not answer the question she has actually been asking, which is whether a real
          woman ever saw the page. `data === null` is a payload in flight and this holds still;
          `data.talkFunnel === null` is a read that arrived and could not be made (R18). */}
      {/* >>> **ONE ASK LALA SECTION, HER ORDER OF 2026-09-05: "there are three different sections in
          analytics for Ask Lala; make it simple and clear."** <<< There were three, in three
          different places, answering three different questions about the same thing: a bare **Lala
          turns** tile in the generic stat row at the top, a **Lala volume** bar chart down beside
          member growth, and the funnel board here. **Nothing is deleted — the two that moved are
          inside this block now**, under one heading, so everything about Ask Lala is read in one
          place and in the order a woman actually walks it: how far she got, then how much was
          spoken, then the shape of it day by day. */}
      <section className="owner-ladder" data-owner-section="ask-lala">
        <OwnerTalkFunnel board={data ? (data.talkFunnel ?? null) : undefined} />
        <div className="owner-stats">
          <div className="owner-stat"><strong>{turns.toLocaleString("en-US")}</strong><span>Lala turns in this window</span></div>
        </div>
        <h3 className="owner-detail__sub">Turns by day</h3>
        <OwnerBars
          rows={lala.map((g) => ({ key: dayLabel(g.day), label: dayLabel(g.day), value: Number(g.turns || 0) }))}
          empty="No turns in this window."
        />
      </section>

      {/* F8 — **DID HER MORNINGS REACH ANYBODY.** It sits under the journey and above the growth
          charts because it is a record of the RAIL rather than of the funnel: everything above it
          is about who arrived, and this is about whether the one thing this house sends to a phone
          every day got there. `data === null` is a payload in flight and this holds still;
          `data.pushDelivery === null` is a read that arrived and could not be made (R18). */}
      <OwnerPushDelivery
        delivery={data ? (data.pushDelivery ?? null) : undefined}
        rangeLabel={data?.range?.label || "this window"}
      />

      {/* CADENCE-1, HER CLAUSE 5 — **IS THE CLIFFHANGER CADENCE EARNING.** It sits under the
          morning rail and above the growth charts for that block's own reason: everything above it
          is about who arrived, and these two are records of the RAILS this house sends on. Her five
          figures in her order, off the ONE declaration the store counts by. `data === null` is a
          payload in flight and this holds still; `data.cadence === null` is a read that arrived and
          could not be made (R18). */}
      <OwnerCadence
        cadence={data ? (data.cadence ?? null) : undefined}
        rangeLabel={data?.range?.label || "this window"}
      />

      <div>
        <div>
          <h3 className="owner-detail__sub">Member growth</h3>
          <OwnerBars
            rows={growth.map((g) => ({ key: dayLabel(g.day), label: dayLabel(g.day), value: Number(g.signups || 0) }))}
            empty="Nobody joined in this window."
          />
        </div>
      </div>

      {/* SURFACE 2(d) — **EVERYTHING ELSE, AT THE BOTTOM, COLLAPSED.** Her two tables by name, and a
          collapsed section draws NO BODY (R27), so a section she has put away costs nothing to keep
          on the page. */}
      <OwnerAnalyticsMore
        open={moreOpen}
        onToggle={() => setMoreOpen((v) => !v)}
        rooms={rooms}
        byType={byType}
      />
    </div>
  );
}
